import { FileResponseInterface } from 'services/interface';

export const cveRegex = /^(CVE-)?[1-2]\d{3}-\d{4,8}$/i;

export const getFileHash = async (file: File) => {
    const fileArrayBuffer = await file.arrayBuffer();
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', fileArrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fileHash = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();

    return fileHash;
};

type GetFileProgress = (processInfoProgress?: number, scanResultsProgress?: number) => number;

export const getFileProgress: GetFileProgress = (processInfoProgress, scanResultsProgress) => {
    if (typeof processInfoProgress === 'undefined' && typeof scanResultsProgress === 'undefined') {
        return 0;
    }

    if (typeof processInfoProgress === 'number' && typeof scanResultsProgress === 'number') {
        return Math.min(processInfoProgress, scanResultsProgress);
    }

    if (typeof processInfoProgress === 'number' && typeof scanResultsProgress === 'undefined') {
        return processInfoProgress;
    }

    if (typeof processInfoProgress === 'undefined' && typeof scanResultsProgress === 'number') {
        return scanResultsProgress;
    }

    return 100;
};


export const isFileScanInProgress = (fileData?: FileResponseInterface | null): boolean => {
    if (!fileData) {
        return true;
    }

    const processProgress = fileData.process_info?.progress_percentage;
    const scanProgress = fileData.scan_results?.progress_percentage;

    if (typeof processProgress === 'number' || typeof scanProgress === 'number') {
        return !getFileComplete(fileData);
    }

    // Only a live 255 means the scan is still running. A record carrying neither progress field nor an
    // aggregate (aborted, errored, a bare `{ file_info: {}, scan_results: {} }`) cannot be shown to be
    // running, and reading it as in-progress pins every caller to an "Analyzing…" state that never ends.
    const aggregate = fileData.scan_results?.scan_all_result_i;

    return aggregate === 255;
};

export const getFileComplete = (fileData?: FileResponseInterface | null): boolean => {
    const fileProgress = getFileProgress(
        fileData?.process_info?.progress_percentage,
        fileData?.scan_results?.progress_percentage,
    );

    const scanInProgress = fileData?.scan_results?.scan_all_result_i === 255;
    const isScanComplete = fileProgress === 100 && !scanInProgress;

    return isScanComplete;
};
